/**
 * Per-scene capture + speaker camera + system/tab audio controls for pitch modules.
 */
import { Camera, Mic, Monitor, Volume2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PITCH_CAPTURE_OPTIONS } from '@/lib/pitch/pitchSegmentConfig';

const CAPTURE_ICONS = { audio: Mic, camera: Camera, screen: Monitor };

export function PitchSceneCaptureControls({
  capture = 'audio',
  speakerCamera = false,
  systemAudio = false,
  disabled = false,
  onChange,
}) {
  const setCapture = (next) => {
    if (disabled || next === capture) return;
    if (next === 'screen') {
      onChange?.({ capture: 'screen', speakerCamera, systemAudio });
      return;
    }
    onChange?.({ capture: next, speakerCamera: false, systemAudio: false });
  };

  const setSpeaker = (on) => {
    if (disabled) return;
    onChange?.({
      capture: on || systemAudio ? 'screen' : capture,
      speakerCamera: on,
      systemAudio: on || systemAudio ? systemAudio : false,
    });
  };

  const setSystem = (on) => {
    if (disabled) return;
    onChange?.({
      capture: on || speakerCamera ? 'screen' : capture,
      speakerCamera: on || speakerCamera ? speakerCamera : false,
      systemAudio: on,
    });
  };

  return (
    <div
      className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 space-y-3"
      data-testid="pitch-scene-capture-controls"
    >
      <div>
        <Label className="text-xs text-slate-400">This scene</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PITCH_CAPTURE_OPTIONS.map((opt) => {
            const Icon = CAPTURE_ICONS[opt.id] || Mic;
            const active = capture === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={disabled}
                data-testid={`pitch-scene-capture-${opt.id}`}
                onClick={() => setCapture(opt.id)}
                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50 ${
                  active
                    ? 'border-amber-400 bg-amber-400/10 text-amber-100'
                    : 'border-slate-700 text-slate-300 hover:border-slate-500'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-700/80 px-3 py-2">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Camera className="h-3.5 w-3.5" /> Speaker camera
          </p>
          <p className="text-[11px] text-slate-500">
            Floats on the shared screen for this scene (turns on screen share)
          </p>
        </div>
        <Switch
          checked={Boolean(speakerCamera)}
          onCheckedChange={setSpeaker}
          disabled={disabled}
          data-testid="pitch-scene-speaker-camera"
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-700/80 px-3 py-2">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Volume2 className="h-3.5 w-3.5" /> System / tab audio
          </p>
          <p className="text-[11px] text-slate-500">
            Mix tab sound with mic for this scene (Chrome: Share tab audio)
          </p>
        </div>
        <Switch
          checked={Boolean(systemAudio)}
          onCheckedChange={setSystem}
          disabled={disabled}
          data-testid="pitch-scene-system-audio"
        />
      </div>
    </div>
  );
}

export default PitchSceneCaptureControls;
